import type { BooruPost, BooruVisibilityType } from "@/lib/booru-api"

export const VIDEO_EXT_REGEX = /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|#|$)/i // Exclude gif/gifv so they display as animated images

const GIF_EXT_REGEX = /\.gif(\?|#|$)/i

/** An animated gif: a .gif file, a post typed "gif", or Paheal's extensionless file known only by its recorded mime. The "GIFs only"
 * switch (Filters, Content Filters) keeps just these. */
export function isGifPost(post: BooruPost): boolean {
  if (post.type === "gif") return true
  if (post.type === "video" || post.type === "album") return false
  if (String(post.rawPost?.mime ?? "").toLowerCase().startsWith("image/gif")) return true
  return GIF_EXT_REGEX.test(String(post.fileUrl || (post as any).file_url || ""))
}

/**
 * Every kind of media a post contains — a post can match more than one, and
 * the content-type filter shows it if ANY of them is switched on. So "Videos
 * only" keeps an album (or gif) with even a single video in it, and "Images
 * only" keeps one with a single image, instead of an album only ever counting
 * as "album" and vanishing from both. Source-agnostic on purpose: it only
 * looks at the post's own URLs and type.
 *
 * A gif counts as both an image and a video — it's stored as an image (so it
 * stays animated) but it is motion, which is what someone filtering for
 * videos expects to see.
 */
export function getPostMediaTypes(post: BooruPost): Set<BooruVisibilityType> {
  const types = new Set<BooruVisibilityType>()

  const addFromUrl = (url: string) => {
    if (VIDEO_EXT_REGEX.test(url)) types.add("video")
    else if (GIF_EXT_REGEX.test(url)) { types.add("image"); types.add("video") }
    else types.add("image")
  }

  if (post.type === "album") {
    types.add("album")
    const items = post.albumImages
    if (items && items.length > 0) {
      items.forEach((url) => addFromUrl(String(url)))
    } else if (post.rawPost?.hasVideo) {
      // Not opened yet, so its contents are unknown — go by the listing's own
      // "contains video" flag (Erome sets it). Anything else is judged by its
      // thumbnail, an image.
      types.add("video")
    } else {
      types.add("image")
    }
    return types
  }

  if (post.type === "video") { types.add("video"); return types }
  if (post.type === "gif") { types.add("image"); types.add("video"); return types }

  // Paheal's file addresses have no extension, so its animated gifs are only recognisable by the mime it recorded (rawPost.mime).
  if (String(post.rawPost?.mime ?? "").toLowerCase().startsWith("image/gif")) {
    types.add("image")
    types.add("video")
    return types
  }

  addFromUrl(String(post.fileUrl || (post as any).file_url || ""))
  return types
}
